/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2026
 */

/**
 * NewArchetypeReportDiscountModalComponent
 *
 * Modal form to create a new archetype report discount code.
 *
 * Super admins can create discount codes that either:
 * - Set a flat fee price (e.g., $0 for free, $29 for reduced price)
 * - Apply a percentage discount (e.g., 20% off)
 *
 * See ArchetypeReportDiscount.model
 * See ArchetypeReport.logic -> calculatePrice()
 */

import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { catchError, of, tap } from 'rxjs';
import { NexleaderInputEnumComponent } from '../../../../core/components/input-enum/input-enum.component';
import { ErrorHandlerService } from '../../../../../services/error-handler.service';
import { SuccessService } from '../../../../../services/success.service';
import { ArchetypeReportDiscountService } from '../../../resources/archetype-report-discount.service';

@Component({
  selector: 'app-new-archetype-report-discount-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NexleaderInputEnumComponent],
  templateUrl: './new-archetype-report-discount-modal.component.html',
})
export class NewArchetypeReportDiscountModalComponent {
  /**
   * Raw discount amount from user input.
   * Will be converted to basis points based on discount type.
   */
  discountAmountRaw: number | null = null;

  /**
   * The discount object being built
   */
  discount: {
    name: string;
    discountType: any;
    discountAmountBps: number | undefined;
    maxUses: number | undefined;
    expiresAt: string | undefined;
    isActive: boolean;
    onePerUser: boolean;
    refreshOnly: boolean;
  } = {
    name: '',
    discountType: null,
    discountAmountBps: undefined,
    maxUses: undefined,
    expiresAt: undefined,
    isActive: true,
    onePerUser: false,
    refreshOnly: false,
  };

  constructor(
    private discountService: ArchetypeReportDiscountService,
    private errorHandlerService: ErrorHandlerService,
    private successService: SuccessService,
    private modalService: BsModalService
  ) {}

  /**
   * Save the discount code
   */
  save(): void {
    // Validation
    if (!this.discount.name || this.discount.name.trim() === '') {
      return this.errorHandlerService.toast({
        message: 'Please enter a discount code name.',
      });
    }

    if (!this.discount.discountType) {
      return this.errorHandlerService.toast({
        message: 'Please select a discount type.',
      });
    }

    if (typeof this.discountAmountRaw !== 'number') {
      return this.errorHandlerService.toast({
        message: 'Please enter a numeric discount amount.',
      });
    }

    // Convert to basis points based on type
    switch (this.discount.discountType.identifier) {
      case 'flat_fee':
      case 'flat_discount':
        // Flat fee: user enters dollar amount, convert to cents
        this.discount.discountAmountBps = this.discountAmountRaw * 100;
        break;
      case 'percentage':
        // Percentage: user enters percentage (e.g., 20 for 20%), convert to basis points
        this.discount.discountAmountBps = this.discountAmountRaw * 100;
        break;
      default:
        return this.errorHandlerService.toast({
          message: 'Please select a valid discount type.',
        });
    }

    this.discountService
      .create(this.discount)
      .pipe(
        tap(() => {
          this.successService.handle({
            message: 'Successfully created archetype report discount code.',
          });
          this.modalService.hide();
        }),
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Get the appropriate symbol for the discount amount input
   */
  getDiscountAmountSymbol(): string | null {
    if (!this.discount.discountType) return null;
    switch (this.discount.discountType.identifier) {
      case 'flat_fee':
      case 'flat_discount':
        return '$';
      case 'percentage':
        return '%';
      default:
        return null;
    }
  }

  /**
   * Close the modal
   */
  close(): void {
    this.modalService.hide();
  }
}
